import { formatDate } from "../../../../utils/Utils";
import {
  typeFormatter,
  formatNumericValue,
} from "../../../../utils/tableDataFormatter";
import { createCleaningEffectivenessCell } from "../../../../utils/cleaningEffectivenessValue";
import { HEADER_STYLE, DATA_STYLE } from "../excelReportStyles";
import {
  parseJSON,
  formatPlantTrainName,
  addBordersToSheet,
} from "../excelReportUtils";

export const createBenchmarkCleanEffectivenessSheet = (sheet, plants, t) => {
  const allCleaningData = [];

  plants.forEach((plant) => {
    if (!(plant?.trains && Array.isArray(plant.trains))) {
      return;
    }

    plant.trains.forEach((train) => {
      const cleaningEfficiency = parseJSON(train.cleaning_efficiency);
      if (!cleaningEfficiency || Object.keys(cleaningEfficiency).length === 0) {
        return;
      }

      const cipData = cleaningEfficiency.cip || {};
      const normPressureDrop = cleaningEfficiency.norm_pressure_drop || {};
      const normPermeateFlow = cleaningEfficiency.norm_permeate_flow || {};
      const normSaltPassage = cleaningEfficiency.norm_salt_passage || {};
      const normWaterPermeability = cleaningEfficiency.norm_water_permeability || {};
      const normPressureDropFlag = cleaningEfficiency.norm_pressure_drop_flag || {};
      const normPermeateFlowFlag = cleaningEfficiency.norm_permeate_flow_flag || {};
      const normSaltPassageFlag = cleaningEfficiency.norm_salt_passage_flag || {};
      const cipReasons = cleaningEfficiency.cip_reason || {};

      Object.keys(cipData).forEach((cipKey) => {
        const cip = cipData[cipKey];
        if (!cip) {
          return;
        }

        const cipReason = cipReasons[cipKey];
        const reasonText = Array.isArray(cipReason)
          ? cipReason.filter((r) => r).join(", ") || "-"
          : cipReason || "-";

        allCleaningData.push({
          plant_id: plant.plant_id,
          plant_name: plant.plant_name,
          train_num: train.train_num,
          type:
            cip.type === "C"
              ? typeFormatter({ value: "C" }, t)
              : cip.type === "S"
                ? typeFormatter({ value: "S" }, t)
                : cip.type,
          start_date: cip.sd,
          cip_reason: reasonText,
          norm_pressure_drop: normPressureDrop[cipKey] ?? null,
          norm_permeate_flow: normPermeateFlow[cipKey] ?? null,
          norm_salt_passage: normSaltPassage[cipKey] ?? null,
          norm_water_permeability: normWaterPermeability[cipKey] ?? null,
          norm_pressure_drop_flag: normPressureDropFlag?.[cipKey] || [],
          norm_permeate_flow_flag: normPermeateFlowFlag?.[cipKey] || [],
          norm_salt_passage_flag: normSaltPassageFlag?.[cipKey] || [],
        });
      });
    });
  });

  const headers = [
    t("benchMark.headers.plantName"),
    t("benchMark.headers.type"),
    t("benchMark.headers.startDate"),
    t("benchMark.headers.cipReason"),
    t("benchMark.headers.pressure"),
    t("benchMark.headers.flow"),
    t("benchMark.headers.saltPassage"),
    t("benchMark.headers.normalizedWaterPermeability"),
  ];

  const headerRow = sheet.getRow(1);
  headers.forEach((header, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = header;
    Object.assign(cell, HEADER_STYLE);
  });
  headerRow.commit();

  allCleaningData.forEach((data, idx) => {
    const row = sheet.getRow(2 + idx);

    const pressureFlags = Array.isArray(data.norm_pressure_drop_flag)
      ? data.norm_pressure_drop_flag.join("")
      : "";
    const flowFlags = Array.isArray(data.norm_permeate_flow_flag)
      ? data.norm_permeate_flow_flag.join("")
      : "";
    const saltFlags = Array.isArray(data.norm_salt_passage_flag)
      ? data.norm_salt_passage_flag.join("")
      : "";

    const values = [
      formatPlantTrainName(data.plant_name, data.plant_id, data.train_num),
      data.type || "-",
      data.start_date ? formatDate(data.start_date) : "-",
      data.cip_reason || "-",
      formatNumericValue(
        createCleaningEffectivenessCell(
          data.norm_pressure_drop,
          pressureFlags || null,
        ),
      ),
      formatNumericValue(
        createCleaningEffectivenessCell(
          data.norm_permeate_flow,
          flowFlags || null,
        ),
      ),
      formatNumericValue(
        createCleaningEffectivenessCell(
          data.norm_salt_passage,
          saltFlags || null,
        ),
      ),
      formatNumericValue(data.norm_water_permeability),
    ];

    values.forEach((value, valueIdx) => {
      const cell = row.getCell(valueIdx + 1);
      cell.value = value;
      Object.assign(cell, DATA_STYLE);
    });

    row.commit();
  });

  sheet.getColumn(1).width = 35;
  sheet.getColumn(2).width = 12;
  sheet.getColumn(3).width = 12;
  sheet.getColumn(4).width = 30;
  for (let i = 5; i <= 8; i++) {
    sheet.getColumn(i).width = 14;
  }
  sheet.getColumn(6).width = 12;
  sheet.getColumn(7).width = 12;
  sheet.getColumn(8).width = 25;

  addBordersToSheet(sheet);
};
